// Libs
import classNames from 'classnames/bind';
import { uid } from 'uid';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { useContext, forwardRef, useCallback, useEffect, useState } from 'react';
import { SimpleTreeItemWrapper, TreeItemComponentProps, TreeItems } from 'dnd-kit-sortable-tree';
import { Box, IconButton, Menu, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseInput, BaseSelect, UnderDevelopment } from '~/components/common';
import { OptionalItemOrderListModal, VariantListOrderModal } from '~/components/module/order';
// Others
import { treeItemSchema } from './helper';
import { IFormOrder } from '~/utils/interface/order';
import { IProductGroup } from '~/utils/interface/quotes';
import { handleFindParentByIdTree } from '~/utils/helper/quote';
import { IListFormVariant, IVariantItem } from '~/utils/interface/variant';
import { OrderStatusEnum, TypeListInventory } from '~/utils/enum/common';
import { EMPTY_STRING, EMPTY_VALUE, optionDiscountType, REG_EXP } from '~/utils/constants/common';
import {
  formatCurrencyUSD,
  formatDurationShort,
  formatInputCurrency,
  handleCalculateDiscountedPrice,
} from '~/utils/helper/common';
import { IBaseOption, IContextTreeItems, IResultCallApiFn, ITreeItem } from '~/utils/interface/common';
import GroupContext from '~/context/groupContext/GroupContext';
// Styles, images, icons
import styles from './ItemGroupOrder.module.scss';
import './ItemGroupOrder.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const ItemGroupOrder = forwardRef<HTMLDivElement, TreeItemComponentProps<ITreeItem<IProductGroup>>>((props, ref) => {
  //#region Destructuring Props
  const { item, onRemove } = props;
  const wrapperProps = {
    ...props,
    showDragHandle: true,
    handleProps: undefined,
  };
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const { items, setItems } = useContext<IContextTreeItems<IProductGroup>>(GroupContext);

  const {
    control,
    setValue,
    watch: watchTreeItem,
    getValues,
    formState: { errors },
  } = useForm<ITreeItem<IProductGroup>>({
    resolver: yupResolver(treeItemSchema(t)),
    defaultValues: item,
    mode: 'onChange',
  });
  const { setValue: setValueFormOrder, watch: watchFormOrder } = useFormContext<IFormOrder>();

  const { level, data: dataItem, id: idTree } = watchTreeItem();
  const {
    id: idItemChange,
    quantity,
    price,
    note,
    type,
    discountType,
    discountRate,
    laborCost,
    isShow,
    isOverbooking,
  } = dataItem as IProductGroup;
  const { orderItems, orderAddOns, deleteItemIds, pricingTotal, status } = watchFormOrder();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [noteAnchor, setNoteAnchor] = useState<{
    anchorEl: HTMLElement | null;
    id: string | null;
  }>({ anchorEl: null, id: null });

  const [isEditName, setIsEditName] = useState<boolean>(false);
  const [isModalVariant, setIsModalVariant] = useState<boolean>(false);
  const [isModalOptional, setIsModalOptional] = useState<boolean>(false);

  const [idRemember, setIdRemember] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const newItems = handleUpdateItemById(items, item?.id, getValues());
    setItems(newItems);
  }, [dataItem, quantity, price, discountType, discountRate, laborCost, isShow]);

  useEffect(() => {
    if (level === 1) return;

    setItems(handleUpdateItemById(items, item?.id, getValues()));
  }, [note]);

  useEffect(() => {
    if (level === 1) return;

    setValue(
      `data.total`,
      handleCalculateDiscountedPrice(price, Number(discountRate), discountType) * Number(quantity)
    );
  }, [quantity, price, discountRate, discountType]);

  useEffect(() => {
    if (level !== 1) return;

    // setValue(`data.price`, handleGetDataLevelPriceTotal(items, item?.id).price);
    setValue(`data.total`, handleGetDataLevelPriceTotal(items, item?.id).total);
  }, [quantity, price, discountRate, item]);

  useEffect(() => {
    if (!item?.data) return;

    setValue('data.quantity', item.data.quantity, {
      shouldDirty: false,
      shouldValidate: true,
    });

    setValue('data.isShow', item.data.isShow, {
      shouldDirty: false,
    });
  }, [item?.data?.quantity, item?.data?.isShow]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseModalOptional = () => {
    setIsModalOptional(false);
  };

  const handleUpdateItemById = (
    items: TreeItems<ITreeItem<IProductGroup>>,
    targetId: string,
    newItem: ITreeItem<IProductGroup>
  ): TreeItems<ITreeItem<IProductGroup>> => {
    return items.map((item: ITreeItem<IProductGroup>) => {
      if (item?.id === targetId && item?.children?.length) {
        return {
          ...newItem,
          children: item?.children,
        };
      } else if (item?.id === targetId) {
        return newItem;
      } else if (item?.children) {
        return {
          ...item,
          children: handleUpdateItemById(item?.children, targetId, newItem),
        };
      } else {
        return item;
      }
    });
  };

  const handleGetDataLevelPriceTotal = (items: TreeItems<ITreeItem<IProductGroup>>, groupId: string) => {
    const group = items?.find((item) => item?.id === groupId);

    if (!group || !group.children) {
      return { price: 0, total: 0 };
    }

    let price = 0;
    let total = 0;

    group.children.forEach((child) => {
      const childData = child?.data;

      if (!childData) return;

      if (childData?.type === TypeListInventory.PACKAGE) {
        const packagePrice =
          handleCalculateDiscountedPrice(
            Number(childData?.price ?? 0),
            Number(childData?.discountRate ?? 0),
            childData?.discountType ?? EMPTY_STRING
          ) || 0;

        price += packagePrice;
        total += childData?.total ?? 0;

        if (child.children?.length) {
          child.children.forEach((grandChild) => {
            const grandChildData = grandChild?.data;
            if (!grandChildData) return;

            if (grandChildData?.isRequired) return;

            price +=
              handleCalculateDiscountedPrice(
                Number(grandChildData?.price ?? 0),
                Number(grandChildData?.discountRate ?? 0),
                grandChildData?.discountType ?? EMPTY_STRING
              ) || 0;

            total += grandChildData?.total ?? 0;
          });
        }

        return;
      }

      price +=
        handleCalculateDiscountedPrice(
          Number(childData?.price ?? 0),
          Number(childData?.discountRate ?? 0),
          childData?.discountType ?? EMPTY_STRING
        ) || 0;

      total += childData?.total ?? 0;

      if (child.children?.length) {
        child.children.forEach((grandChild) => {
          const grandChildData = grandChild?.data;
          if (!grandChildData) return;

          price +=
            handleCalculateDiscountedPrice(
              Number(grandChildData?.price ?? 0),
              Number(grandChildData?.discountRate ?? 0),
              grandChildData?.discountType ?? EMPTY_STRING
            ) || 0;

          total += grandChildData?.total ?? 0;
        });
      }
    });

    return { price, total };
  };

  const fetchOptions = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionDiscountType,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleShowNote = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    setNoteAnchor({ anchorEl: event?.currentTarget, id });
  };

  const handleCloseNote = () => {
    setNoteAnchor({ anchorEl: null, id: null });
  };

  const handleChangeName = () => {
    if (status === OrderStatusEnum.FINALIZED) return;

    setIsEditName(true);
  };

  const handleFindGroupIdByChildDataId = (
    listGroup: TreeItems<ITreeItem<IProductGroup>>,
    targetNodeId: string
  ): number | string => {
    for (const group of listGroup) {
      if (group?.level === 1 && group?.children?.length) {
        const hasMatchingChild = group?.children?.some((child) => child?.id === targetNodeId);

        if (hasMatchingChild) {
          return group?.id ?? EMPTY_STRING;
        }
      }
    }

    return EMPTY_STRING;
  };

  const handleCloseModalVariant = () => {
    setIsModalVariant(false);
  };

  const handleSaveVariant = (data: IListFormVariant, idRemember: string) => {
    if (!data?.listVariant) return;

    const selectedVariants = data?.listVariant?.filter((item) => item?.isChecked);

    const newItems = idRemember
      ? handleReplaceItemAddOnByIdRemember(items, idRemember, selectedVariants)
      : handleReplaceItemAddOnWithVariants(items, item?.id, selectedVariants);

    setItems(newItems);

    setIsModalVariant(false);
  };

  const handleReplaceItemAddOnByIdRemember = (
    listGroups: TreeItems<ITreeItem<IProductGroup>>,
    targetIdRemember: string,
    variants: IVariantItem[]
  ): TreeItems<ITreeItem<IProductGroup>> => {
    let hasInserted = false;
    let templateItem: ITreeItem<IProductGroup> | null = null;

    const buildNewNodes = (baseItem: ITreeItem<IProductGroup>) =>
      variants?.map((variant, index) => ({
        id: uid(),
        level: baseItem?.level,
        parentId: baseItem?.parentId,
        canHaveChildren: false,
        data: {
          ...baseItem?.data,
          id: baseItem?.data?.id || EMPTY_STRING,
          variantName: variant?.name || EMPTY_STRING,
          price: Number(variant?.rentalPrice),
          quantity: Number(variant?.quantity),
          availableInventory: variant?.quantityInventory,
          total: Number(variant?.quantity) * Number(variant?.rentalPrice),
          discountType: baseItem?.data?.discountType || EMPTY_STRING,
          discountRate: baseItem?.data?.discountRate || EMPTY_STRING,
          note: baseItem?.data?.note || EMPTY_STRING,
          variantId: variant?.id || EMPTY_STRING,
          isHasVariant: index === 0,
          idRemember: targetIdRemember || EMPTY_STRING,
        },
        children: [],
      })) || [];

    const traverse = (nodes: TreeItems<ITreeItem<IProductGroup>>): TreeItems<ITreeItem<IProductGroup>> => {
      const result: ITreeItem<IProductGroup>[] = [];

      for (const node of nodes) {
        let updatedChildren = node?.children ? traverse(node?.children) : node?.children;

        if (node?.data?.idRemember === targetIdRemember) {
          if (!templateItem) {
            templateItem = { ...node, children: [] };
          }
          if (!hasInserted && templateItem) {
            result.push(...buildNewNodes(templateItem));
            hasInserted = true;
          }
          continue;
        }

        result.push({ ...node, children: updatedChildren });
      }

      return result;
    };

    const updated = traverse(listGroups);

    if (!hasInserted && templateItem) {
      return [...updated, ...buildNewNodes(templateItem)];
    }

    return updated;
  };

  const handleReplaceItemAddOnWithVariants = (
    listGroups: TreeItems<ITreeItem<IProductGroup>>,
    targetId: string,
    variants: IVariantItem[]
  ): TreeItems<ITreeItem<IProductGroup>> => {
    return listGroups.reduce<ITreeItem<IProductGroup>[]>((acc, itemList) => {
      if (itemList?.id === targetId) {
        const newNodes = variants?.map((variant, index) => ({
          id: uid(),
          level: itemList?.level,
          parentId: itemList?.parentId,
          canHaveChildren: false,
          data: {
            ...itemList?.data,
            id: itemList?.data?.id || EMPTY_STRING,
            variantName: variant?.name || EMPTY_STRING,
            price: Number(variant?.rentalPrice),
            quantity: Number(variant?.quantity),
            availableInventory: variant?.quantityInventory,
            total: Number(variant?.quantity) * Number(variant.rentalPrice),
            discountType: itemList?.data?.discountType || EMPTY_STRING,
            discountRate: itemList?.data?.discountRate || EMPTY_STRING,
            note: itemList?.data?.note || EMPTY_STRING,
            variantId: variant?.id || EMPTY_STRING,
            isHasVariant: index === 0,
            idRemember: item?.id || EMPTY_STRING,
          },
          children: [],
        }));
        return [...acc, ...(newNodes || [])];
      } else if (itemList?.children && itemList?.children?.length > 0) {
        return [
          ...acc,
          {
            ...itemList,
            children: handleReplaceItemAddOnWithVariants(itemList?.children, targetId, variants),
          },
        ];
      }
      return [...acc, itemList];
    }, []);
  };

  const handleCollectVariantIdsByRemember = (
    listGroups: TreeItems<ITreeItem<IProductGroup>>,
    targetIdRemember: string
  ): string[] => {
    const result: string[] = [];

    const traverse = (nodes: TreeItems<ITreeItem<IProductGroup>>) => {
      for (const node of nodes) {
        if (node?.data?.idRemember === targetIdRemember && node?.data?.variantId) {
          result.push(node?.data?.variantId);
        }
        if (node?.children && node?.children?.length > 0) {
          traverse(node?.children);
        }
      }
    };

    traverse(listGroups);

    return result;
  };

  const handleSumQuantity = (inventoryId: string, variantId: string, availableInventory?: string) => {
    if (dataItem?.isHasVariant && !dataItem?.variantId) {
      return false;
    }

    const newData = [...(orderItems ?? []), ...(orderAddOns ?? [])];

    const sum = newData
      .filter(
        (item) => item?.inventoryId === inventoryId && (item?.variantId || EMPTY_STRING) === (variantId || EMPTY_STRING)
      )
      .reduce((sum, item) => sum + Number(item?.quantity), 0);

    const isOverbookingData = sum > Number(availableInventory) - Number(dataItem?.overbookedQuantity || 0);

    return isOverbookingData;
  };

  const handleRemove = () => {
    if (dataItem?.isRequired) return;

    if (level === 3 && !dataItem?.isRequired) {
      const parentNode = handleFindParentByIdTree(items, item?.id);

      if (parentNode?.data?.type === TypeListInventory.PACKAGE) {
        setValue('data.isShow', false);
      }
    }

    if (level === 1) {
      const findGroup = items.find((item) => item?.id === getValues()?.id);
      const childrenProductId: string[] = findGroup?.children?.map((item) => String(item?.data?.id)) || [];

      setValueFormOrder('deleteChildrenProduct', childrenProductId);
    } else {
      setValueFormOrder('deleteProduct', uid());
      setValueFormOrder('selectQuantityProduct', 0);
      setValueFormOrder('selectIdProductGroup', dataItem?.inventoryId);
      setValueFormOrder('selectIdVariantGroup', dataItem?.variantId || EMPTY_STRING);
    }

    if (dataItem?.idDetail && !isNaN(Number(dataItem.idDetail))) {
      setValueFormOrder('deleteItemIds', [...(deleteItemIds || []), String(dataItem?.idDetail)]);
    }

    onRemove?.();
  };

  const handleChangeQuantity = () => {
    setValueFormOrder('selectIdProductGroup', dataItem?.inventoryId);
    setValueFormOrder('selectIdVariantGroup', dataItem?.variantId || EMPTY_STRING);
    setValueFormOrder('selectGroupItem', String(handleFindGroupIdByChildDataId(items, String(item?.id))));
  };

  const updateNodeChildrenById = (
    nodes: TreeItems<ITreeItem<IProductGroup>>,
    targetId: string,
    newChildren: TreeItems<ITreeItem<IProductGroup>>
  ): TreeItems<ITreeItem<IProductGroup>> => {
    return nodes.map((node) => {
      if (node?.id === targetId) {
        return { ...node, children: newChildren };
      }
      if (node?.children?.length) {
        return {
          ...node,
          children: updateNodeChildrenById(node.children, targetId, newChildren),
        };
      }
      return node;
    });
  };

  const handleSaveOptional = (data: IListFormVariant) => {
    if (!data?.listVariant) return;

    const selectedOptionals = data.listVariant.filter((opt) => opt?.isChecked);
    const currentPackageChildren = item?.children ?? [];

    const removedOptionals = currentPackageChildren.filter(
      (child) =>
        child?.level === 3 &&
        child?.data?.isAddOn &&
        !child?.data?.isRequired &&
        !selectedOptionals.some(
          (opt) =>
            String(child?.data?.inventoryId) === String(opt?.inventoryId) &&
            String(child?.data?.variantId || EMPTY_STRING) === String(opt?.variantId || EMPTY_STRING)
        )
    );

    const removedIdDetails = removedOptionals
      .map((c) => c?.data?.idDetail)
      .filter((idDetail): idDetail is string => Boolean(idDetail));

    if (removedIdDetails?.length > 0) {
      setValueFormOrder('deleteItemIds', [...(deleteItemIds || []), ...removedIdDetails]);
    }

    const newChildren = updateChildrenBySelectedOptionals(currentPackageChildren, selectedOptionals);
    const updatedItems = updateNodeChildrenById(items, item?.id, newChildren);

    setItems(updatedItems);
    setIsModalOptional(false);
  };

  const updateChildrenBySelectedOptionals = (children: any[], selectedOptionals: IVariantItem[]) => {
    const requiredChildren = children.filter((child) => child.data?.isRequired === true);

    const optionalChildren = selectedOptionals.map((opt) => {
      const existed = children.find(
        (c) =>
          String(c.data?.inventoryId) === String(opt?.inventoryId) &&
          String(c.data?.variantId || EMPTY_STRING) === String(opt?.variantId || EMPTY_STRING)
      );

      if (existed) {
        return {
          ...existed,
          data: {
            ...existed.data,
            quantity: Number(opt.quantity),
            isShow: true,
          },
        };
      }

      return {
        id: crypto.randomUUID(),
        level: 3,
        canHaveChildren: false,
        data: {
          id: opt?.inventoryId ?? EMPTY_STRING,
          idDetail: undefined,
          inventoryId: opt.inventoryId,
          variantId: opt.variantId,
          name: opt.name,
          variantName: opt.name || EMPTY_STRING,
          isAddOn: true,
          price: Number(opt.rentalPrice),
          quantity: Number(opt.quantity),
          availableInventory: opt.quantityInventory,
          total: Number(opt.quantity) * Number(opt.rentalPrice),
          isRequired: false,
          isShow: true,
        },
      };
    });

    return [...requiredChildren, ...optionalChildren];
  };

  const handleSelectItemPackage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOptional(true);
  };
  //#endregion Handle Function

  if (item?.data?.isShow === false) {
    return null;
  } else {
    return (
      <Box id='itemGroupOrderComponent' key={item?.id}>
        <SimpleTreeItemWrapper
          {...wrapperProps}
          ref={ref}
          disableCollapseOnItemClick
          disableInteraction={status === OrderStatusEnum.FINALIZED}
          disableSelection={status === OrderStatusEnum.FINALIZED}
          disableSorting={status === OrderStatusEnum.FINALIZED}
          showDragHandle={false}
          hideCollapseButton={level === 2}
          className={cx(level === 3 ? 'subChildren' : level === 1 ? 'parent' : 'children')}
        >
          <Table stickyHeader>
            <TableBody>
              <TableRow
                sx={{
                  backgroundColor:
                    (dataItem?.type !== TypeListInventory.PACKAGE &&
                      handleSumQuantity(dataItem?.inventoryId!, dataItem?.variantId!, dataItem?.availableInventory)) ||
                    Boolean(dataItem?.isOverbooking)
                      ? '#FFE7ED'
                      : 'inherit',
                }}
              >
                <TableCell width={'20%'} className={cx('textCell')}>
                  <Box className={cx('textCell', 'viewRow')}>
                    {dataItem?.isAddOn || level === 3 ? (
                      <Box className={cx('textAdd')}>
                        {dataItem?.type === TypeListInventory.PRODUCT &&
                        handleFindParentByIdTree(items, item?.id)?.data?.type === TypeListInventory.PACKAGE
                          ? t('form_groups_title_product_item')
                          : t('form_groups_title_add_on')}
                      </Box>
                    ) : (
                      <IconButton {...props.handleProps} className={cx('iconDrag')}>
                        <img src={icons.commonIconRemove} alt={t('common_alt_img', { ns: 'common/base' })} />
                      </IconButton>
                    )}

                    {isEditName && level === 1 ? (
                      <Controller
                        name={`data.name`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <BaseInput
                            multiline
                            disabled={status === OrderStatusEnum.FINALIZED}
                            height={'auto'}
                            isBorder={false}
                            value={value}
                            onChange={onChange}
                            placeholder={t('form_orders_placeholder_name')}
                            onBlur={() => setIsEditName(false)}
                          />
                        )}
                      />
                    ) : (
                      <Stack direction={'row'} gap={2} alignItems={'center'}>
                        <Stack direction={'column'} gap={1}>
                          <Box className={cx('textName')} onClick={handleChangeName}>
                            {dataItem?.name}
                          </Box>

                          {dataItem?.variantName && <Box className={cx('textVariant')}>{dataItem?.variantName}</Box>}

                          {dataItem?.isHasVariant && !dataItem?.idRemember && (
                            <Typography className={cx('textVariantError')}>{t('form_orders_variant_error')}</Typography>
                          )}

                          {dataItem?.isRequired && (
                            <Box className={cx('textRequired')}>{t('common_text_required', { ns: 'common/base' })}</Box>
                          )}
                        </Stack>

                        {dataItem?.type === TypeListInventory.PACKAGE && (
                          <Box className={cx('viewBox')}>{t('form_orders_label_package')}</Box>
                        )}
                      </Stack>
                    )}

                    {dataItem?.isDelete && (
                      <Box className={cx('delete')}>{t('common_button_delete', { ns: 'common/base' })}</Box>
                    )}

                    <IconButton
                      className={cx('buttonIcon', { disabled: status === OrderStatusEnum.FINALIZED })}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleShowNote(event, item?.id);
                      }}
                    >
                      <img
                        className={cx('iconNote')}
                        src={dataItem?.note ? icons.commonIconAddNoteActive : icons.commonIconAddNote}
                        alt={t('common_alt_img', { ns: 'common/base' })}
                      />
                    </IconButton>

                    {noteAnchor?.id === item?.id && (
                      <Menu
                        open={Boolean(noteAnchor?.anchorEl)}
                        anchorEl={noteAnchor?.anchorEl}
                        onClose={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          handleCloseNote();
                        }}
                        slotProps={{
                          paper: {
                            className: cx('paper'),
                          },
                        }}
                        MenuListProps={{ className: cx('menuList') }}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        autoFocus={false}
                      >
                        <Box className={cx('viewNote')}>
                          <Controller
                            name={`data.note`}
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <BaseInput
                                multiline
                                disabled={status === OrderStatusEnum.FINALIZED}
                                height={'auto'}
                                rows={6}
                                isBorder={false}
                                value={value}
                                onChange={onChange}
                                placeholder={t('form_orders_table_title_note')}
                              />
                            )}
                          />
                        </Box>
                      </Menu>
                    )}
                  </Box>

                  {dataItem?.note && (
                    <Typography className={cx('textNote')}>{`${t('form_orders_table_title_note_value')} ${
                      dataItem.note
                    }`}</Typography>
                  )}
                </TableCell>

                <TableCell width={'15%'}>
                  <Box className={cx('textCell', 'viewRow')}>
                    {level !== 1 && (
                      <Controller
                        name={`data.discountType`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <BaseSelect
                            height={36}
                            width={82}
                            disabled={status === OrderStatusEnum.FINALIZED}
                            styleType='extra'
                            placeholder={t('form_orders_placeholder_percent')}
                            callApiFn={fetchOptions}
                            onChange={(data) => {
                              onChange(data?.value);
                            }}
                            value={value}
                          />
                        )}
                      />
                    )}

                    {level !== 1 && (
                      <Controller
                        name={`data.discountRate`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <BaseInput
                            height={32}
                            disabled={status === OrderStatusEnum.FINALIZED}
                            width={66}
                            borderRadius={'4px'}
                            value={value}
                            onChange={(event) => {
                              onChange(formatInputCurrency(event?.target?.value));
                            }}
                          />
                        )}
                      />
                    )}
                  </Box>
                </TableCell>

                <TableCell width={'12%'}>
                  <Box className={cx('textCell', 'viewRow')}>
                    {/* {type === TypeListInventory.PACKAGE ? (
                    <>
                      <Typography className={cx(discountRate && 'viewPrice')}>
                        {formatCurrencyUSD({ value: dataItem?.price, showDecimal: true })}
                      </Typography>

                      {!!discountRate && (
                        <>
                          <Typography>/</Typography>
                          <Typography>
                            {formatCurrencyUSD({
                              value: handleCalculateDiscountedPrice(price, Number(discountRate), discountType),
                              showDecimal: true,
                            })}
                          </Typography>
                        </>
                      )}
                    </>
                  ) : ( */}

                    {level !== 1 && (
                      <Controller
                        name={`data.price`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <BaseInput
                            height={32}
                            width={66}
                            borderRadius={'4px'}
                            value={String(value || 0)}
                            disabled={status === OrderStatusEnum.FINALIZED}
                            onChange={(event) => {
                              onChange(formatInputCurrency(event?.target?.value));
                            }}
                          />
                        )}
                      />
                    )}
                    {/* )} */}
                  </Box>
                </TableCell>

                <TableCell width={'8%'} className={cx('textCell')}>
                  {level !== 1 && (
                    <Controller
                      name={`data.laborCost`}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <BaseInput
                          height={32}
                          width={66}
                          borderRadius={'4px'}
                          disabled={status === OrderStatusEnum.FINALIZED}
                          value={value}
                          onChange={(event) => {
                            onChange(formatInputCurrency(event?.target?.value));
                          }}
                        />
                      )}
                    />
                  )}
                </TableCell>

                <TableCell width={'18%'}>
                  <Stack alignItems={'center'} gap={3}>
                    <Controller
                      name={`data.quantity`}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <>
                          {level !== 1 && (
                            <Stack
                              direction='row'
                              spacing={1}
                              alignItems='center'
                              gap={2}
                              className={cx('viewQuantity')}
                            >
                              <IconButton
                                className={cx('buttonQty')}
                                disabled={status === OrderStatusEnum.FINALIZED}
                                onClick={() => {
                                  const newQty = Math.max(Number(value) - 1, 0);
                                  setValue(`data.quantity`, newQty, { shouldDirty: true, shouldValidate: true });
                                  if (level === 2 && !dataItem?.isAddOn) {
                                    setValueFormOrder('selectQuantityProduct', newQty || 0);
                                    handleChangeQuantity();
                                  }
                                }}
                              >
                                <img src={icons.commonIconMinus} alt={t('common_alt_img')} />
                              </IconButton>

                              <BaseInput
                                height={31}
                                textAlignCenter
                                width={55}
                                borderRadius={'4px'}
                                disabled={status === OrderStatusEnum.FINALIZED}
                                value={String(value)}
                                onChange={(e) => {
                                  const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                                  const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                                  onChange(numericValue);
                                  if (level === 2 && !dataItem?.isAddOn) {
                                    setValueFormOrder('selectQuantityProduct', numericValue || 0);
                                    handleChangeQuantity();
                                  }
                                }}
                              />

                              <IconButton
                                className={cx('buttonQty')}
                                disabled={status === OrderStatusEnum.FINALIZED}
                                onClick={() => {
                                  const newQty = Number(value) + 1;
                                  setValue(`data.quantity`, newQty, { shouldDirty: true, shouldValidate: true });
                                  if (level === 2 && !dataItem?.isAddOn) {
                                    setValueFormOrder('selectQuantityProduct', newQty || 0);
                                    handleChangeQuantity();
                                  }
                                }}
                              >
                                <img src={icons.commonIconPlusGrey} alt={t('common_alt_img')} />
                              </IconButton>
                            </Stack>
                          )}
                        </>
                      )}
                    />

                    {errors?.data?.quantity?.message && (
                      <Box className={cx('overbooked')}>{errors?.data?.quantity?.message}</Box>
                    )}

                    {((dataItem?.type !== TypeListInventory.PACKAGE &&
                      handleSumQuantity(dataItem?.inventoryId!, dataItem?.variantId!, dataItem?.availableInventory)) ||
                      Boolean(dataItem?.isOverbooking)) && <Box className={cx('overbooked')}>{t('OVERBOOKED')}</Box>}
                  </Stack>
                </TableCell>

                <TableCell width={'12%'} className={cx('textCell')}>
                  {item?.parent?.data?.type === TypeListInventory.PACKAGE && item?.data?.isShowPrice ? (
                    <></>
                  ) : (
                    formatCurrencyUSD({
                      value: Number(dataItem?.total || 0) * Number(pricingTotal || 1),
                      showDecimal: true,
                    })
                  )}
                </TableCell>

                <TableCell width={'5%'}>
                  <Stack direction={'row'} gap={2}>
                    {dataItem?.isHasVariant ? (
                      <IconButton
                        disabled={status === OrderStatusEnum.FINALIZED}
                        className={cx(
                          'buttonIcon',
                          dataItem?.isHasVariant && !dataItem?.idRemember ? 'buttonIconError' : 'buttonIconChoose'
                        )}
                        onClick={() => {
                          setIsModalVariant(true);
                          setIdRemember(dataItem?.idRemember || EMPTY_STRING);
                        }}
                      >
                        <img
                          className={cx('iconVariant')}
                          src={icons.commonIconEditVariant}
                          alt={t('common_alt_img', { ns: 'common/base' })}
                        />
                      </IconButton>
                    ) : (
                      <Box className={cx('iconEm')} />
                    )}

                    {item?.data?.type === TypeListInventory.PACKAGE && (
                      <IconButton
                        disabled={status === OrderStatusEnum.FINALIZED}
                        className={cx('buttonOption')}
                        onClick={(e) => handleSelectItemPackage(e)}
                      >
                        <img
                          className={cx('iconVariant')}
                          src={icons.commonIconEditVariant}
                          alt={t('common_alt_img', { ns: 'common/base' })}
                        />
                      </IconButton>
                    )}

                    <IconButton
                      className={cx('buttonIcon')}
                      onClick={handleRemove}
                      disabled={status === OrderStatusEnum.FINALIZED}
                    >
                      <img
                        className={cx('iconDelete')}
                        src={dataItem?.isRequired ? icons.commonIconTrashDisable : icons.commonIconTrash}
                        alt={t('common_alt_img', { ns: 'common/base' })}
                      />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </SimpleTreeItemWrapper>

        {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

        {isModalVariant && (
          <VariantListOrderModal
            onClose={handleCloseModalVariant}
            parentId={dataItem?.productId}
            productId={dataItem?.addOnId}
            inventoryId={dataItem?.inventoryId}
            idRemember={idRemember}
            idVariants={handleCollectVariantIdsByRemember(items, idRemember)}
            detailsData={handleFindParentByIdTree(items, item?.id)?.children}
            onSave={handleSaveVariant}
          />
        )}

        {isModalOptional && (
          <OptionalItemOrderListModal
            onClose={handleCloseModalOptional}
            inventoryId={dataItem?.inventoryId}
            onSave={handleSaveOptional}
          />
        )}
      </Box>
    );
  }
});

export default ItemGroupOrder;
